/* eslint-disable no-shadow */
/* eslint-disable react/no-array-index-key */
import { ArticleType } from '@/pages/[categorias]/[...news]';
import { WidgetType } from '@/types/types';
import { DealsProps } from '@/components/Layout/DealsBox';
import { getCurrentHub } from '@sentry/nextjs';
import { Fragment, useState } from 'react';
import dynamic from 'next/dynamic';
import useBreakpoint from '@/hooks/useBreakpoint';
import { contentRearranger } from '@/components/Ads/Rearrangers';
import AdMembrana from '@/components/Ads/AdMembrana';
import AdTruvid from '@/components/Ads/AdTruvid';
import { DivContentNews } from './styles';
import CustomComponentComposer, { isCustomComponent } from '../CustomComponentComposer';
import ContentComposer from '../ContentComposer';
import classNames from '@/utils/className';
import ChannelsCard from '@/components/Layout/ChannelsCard';
import AdsGeneratorMemo from '@/components/Ads/Google/AdsTypeGeneratorsMemo';

const MoreAbout = dynamic(() => import('../Widgets/MoreAbout/MoreAbout'));

type NewsContentProps = {
  dataContent: ArticleType;
  widget?: WidgetType;
  placeholderAds?: boolean;
  shouldRenderAdsGoogle?: boolean;
  isSubDomain?: boolean;
  product?: DealsProps;
  titulo?: string;
  hasPartnerLinks?: boolean;
  isNewsOpen?: boolean;
  useMembrana?: boolean;
  mainCategory?: string
};

export default function NewsContent({
  dataContent,
  widget = null,
  placeholderAds = false,
  shouldRenderAdsGoogle = true,
  isSubDomain = false,
  product = null,
  titulo = '',
  hasPartnerLinks = false,
  isNewsOpen = false,
  useMembrana = false,
  mainCategory = '',
}: NewsContentProps) {
  const transaction = getCurrentHub().getScope()?.getTransaction();
  const newsContentSpan = transaction && transaction.startChild({ op: 'newsContent' });
  const chunckContent = contentRearranger({
    content: dataContent.conteudo,
    useTruvid: !useMembrana && !hasPartnerLinks,
    useMembrana: useMembrana && !hasPartnerLinks,
  });

  const [isMobile] = useBreakpoint('phone');
  const [isOpen, setOpened] = useState(isNewsOpen);

  const handleClickOpened = () => {
    setOpened(!isOpen);
  };

  const whenMobileAndShortContent = isMobile && dataContent.conteudo.length < 9;

  return (
    <>
      {!!widget && (
        <MoreAbout
          type={widget.tipo}
          dados={widget.dados}
          isSubDomain={isSubDomain}
          product={product && { preco: product.preco, encurtador: product.encurtador || product.url }}
        />
      )}
      <DivContentNews>
        {chunckContent?.map((partHtml, index) => {
          const idCountPosition = partHtml.idCountPosition ? partHtml.idCountPosition : '';
          const whenMobileAndLongContentAndReadMore = isMobile && dataContent.conteudo.length >= 8 && chunckContent.indexOf(partHtml) <= 14 && !isOpen;
          const shouldRenderFullWhenMainCategoryIsMatch = ['Ciência', 'Entretenimento'].includes(mainCategory);
          const shouldShow = shouldRenderFullWhenMainCategoryIsMatch || isOpen || !isMobile || whenMobileAndShortContent || whenMobileAndLongContentAndReadMore;

          if (partHtml.tipo === 'cardCTA') {
            return (
              <Fragment key={index}>
                <ChannelsCard />
              </Fragment>
            );
          }

          if (partHtml.tipo === 'adMobile') {
            const adMobileSpan =
              newsContentSpan &&
              newsContentSpan.startChild({
                op: 'adRectangleMainInternalM',
              });
            return (
              isMobile &&
              shouldRenderAdsGoogle && (
                <div className={classNames(shouldShow ? 'flex' : 'hidden', ' min-h-[340px] flex-col')} key={`${index}`}>
                  <span className="mb-1 h-3 text-center text-[10px] uppercase tracking-[0.05em] text-mglGray500">Continua após a publicidade</span>
                  <AdsGeneratorMemo
                    isPlaceholder={placeholderAds}
                    key={index}
                    type="rectangle/main/internal"
                    marginBottomP="24px"
                    idCountPosition={idCountPosition}
                  />
                  {adMobileSpan && adMobileSpan.finish()}
                </div>
              )
            );
          }
          if (partHtml.tipo === 'adDesktop') {
            const adDesktopSpan =
              newsContentSpan &&
              newsContentSpan.startChild({
                op: 'adRectangleMainInternalD',
              });
            return (
              !isMobile &&
              shouldRenderAdsGoogle && (
                <div className={classNames(shouldShow ? 'flex' : 'hidden', ' min-h-[340px] flex-col')} key={`${index}`}>
                  <span className="mb-1 h-3 text-center text-[10px] uppercase tracking-[0.05rem] text-mglGray500">Continua após a publicidade</span>
                  <AdsGeneratorMemo
                    isPlaceholder={placeholderAds}
                    key={index}
                    type="rectangle/main/internal"
                    marginBottomP="24px"
                    idCountPosition={idCountPosition}
                  />
                  {adDesktopSpan && adDesktopSpan.finish()}
                </div>
              )
            );
          }
          if (partHtml.tipo === 'adMembranaDesktop' && !isMobile) {
            const adMembranaSpan =
              newsContentSpan &&
              newsContentSpan.startChild({
                op: 'adMembrana',
              });
            return (
              !isMobile &&
              !hasPartnerLinks && (
                <div
                  className={classNames(
                    shouldShow ? 'block' : 'hidden',
                    'z-[2] mx-auto mb-4 aspect-video h-full w-full items-center justify-center bg-black/5',
                    placeholderAds &&
                      "bg-mglGray200 text-mglGray300 before:flex before:h-full before:items-center before:justify-center before:content-['PUBLICIDADE']"
                  )}
                  key={`${index}`}>
                  {!placeholderAds && <AdMembrana />}
                  {adMembranaSpan && adMembranaSpan.finish()}
                </div>
              )
            );
          }
          if (partHtml.tipo === 'adTruvidDesktop' && !isMobile) {
            const adTruvidSpan =
              newsContentSpan &&
              newsContentSpan.startChild({
                op: 'adTruvid',
              });
            return (
              !isMobile &&
              !hasPartnerLinks && (
                <div
                  className={classNames(
                    shouldShow ? 'block' : 'hidden',
                    'z-[2] mx-auto mb-4 aspect-video h-full w-full items-center justify-center bg-black/5 [&>.truvidPos]:grid [&>.truvidPos]:aspect-video [&>.truvidPos]:items-center',
                    placeholderAds &&
                      "bg-mglGray200 text-mglGray300 before:flex before:h-full before:items-center before:justify-center before:content-['PUBLICIDADE']"
                  )}
                  key={`${index}`}>
                  {!placeholderAds && <AdTruvid />}
                  {adTruvidSpan && adTruvidSpan.finish()}
                </div>
              )
            );
          }
          if (partHtml.tipo === 'adMembranaMobile' && isMobile) {
            const adMembranaSpan =
              newsContentSpan &&
              newsContentSpan.startChild({
                op: 'adMembrana',
              });
            return (
              isMobile &&
              !hasPartnerLinks && (
                <div
                  className={classNames(
                    shouldShow ? 'block' : 'hidden',
                    'z-[2] mx-auto mb-4 aspect-video h-full w-full items-center justify-center self-start',
                    placeholderAds &&
                      "bg-mglGray200 text-mglGray300 before:flex before:h-full before:items-center before:justify-center before:content-['PUBLICIDADE']"
                  )}
                  key={`${index}`}>
                  {!placeholderAds && <AdMembrana />}
                  {adMembranaSpan && adMembranaSpan.finish()}
                </div>
              )
            );
          }
          if (partHtml.tipo === 'adTruvidMobile' && isMobile) {
            const adTruvidSpan =
              newsContentSpan &&
              newsContentSpan.startChild({
                op: 'adTruvid',
              });
            return (
              isMobile &&
              !hasPartnerLinks && (
                <div
                  className={classNames(
                    shouldShow ? 'block' : 'hidden',
                    'z-[2] mx-auto mb-4 aspect-video h-full w-full items-center justify-center self-start [&>.truvidPos]:grid [&>.truvidPos]:aspect-video [&>.truvidPos]:items-center [&>.truvidPos]:bg-black/5',
                    placeholderAds &&
                      "bg-mglGray200 text-mglGray300 before:flex before:h-full before:items-center before:justify-center before:content-['PUBLICIDADE']"
                  )}
                  key={`${index}`}>
                  {!placeholderAds && <AdTruvid />}
                  {adTruvidSpan && adTruvidSpan.finish()}
                </div>
              )
            );
          }
          if (isCustomComponent(partHtml)) {
            const customComposerSpan =
              newsContentSpan &&
              newsContentSpan.startChild({
                op: 'customComposer',
                description: JSON.stringify(partHtml),
              });
            return (
              <div className={classNames(shouldShow ? 'block' : 'hidden')} key={`${index}`}>
                <CustomComponentComposer partHtml={partHtml} index={index} key={index} titulo={titulo} />
                {customComposerSpan && customComposerSpan.finish()}
              </div>
            );
          }

          if (partHtml.tipo === 'readMore' && !shouldRenderFullWhenMainCategoryIsMatch) {
            return (
              <Fragment key={index}>
                {!isOpen && isMobile && (
                  <div className="relative z-[2] -ml-4 -mt-36 flex h-[200px] w-[calc(100%_+_2rem)] flex-col justify-end bg-gradient-to-t from-mglBlue400 via-mglBlue300 to-75% px-6">
                    <button
                      className="mb-12 flex w-full items-center justify-center rounded-[2rem] border-0 bg-mglGray900 p-2 text-base font-medium text-mglGray50 outline-none transition-all ease-in-out [&_svg]:ml-2 [&_svg]:fill-mglGray50"
                      id="read-more"
                      onClick={handleClickOpened}>
                      continuar lendo
                    </button>
                  </div>
                )}
              </Fragment>
            );
          }

          const contentComposerSpan =
            newsContentSpan &&
            newsContentSpan.startChild({
              op: 'contentComposer',
              description: JSON.stringify(partHtml),
            });
          return (
            <Fragment key={`${index}`}>
              <ContentComposer
                tipo={partHtml.tipo}
                as={partHtml.tag}
                url={partHtml.url}
                target={partHtml.target}
                valor={partHtml?.valor}
                atributos={partHtml?.atributos}
                key={`${partHtml.tipo}-${index}`}
                className={classNames(shouldShow ? '' : 'hidden')}
              />
              {contentComposerSpan && contentComposerSpan.finish()}
            </Fragment>
          );
        })}
      </DivContentNews>
      {newsContentSpan && newsContentSpan.finish()}
    </>
  );
}
